import { GetStaticPaths, GetStaticProps } from "next";
import { NextSeo } from "next-seo";

import { TOOL_CONTENT_MAP } from "@/lib/tools/content";

import AutoClipperSection from "@/components/home/AutoClipperSection";
import { CoFounder } from "@/components/home/CoFounder";
import FeaturesBento from "@/components/home/FeaturesSection";
import StepsSection from "@/components/home/StepsSection";
import Testimonials from "@/components/home/Testimonials";
import FAQ from "@/components/shared/FAQ";
import Footer from "@/components/shared/Footer";
import ToolHero from "@/components/tools/ToolHero";
import Breadcrumbs from "@/components/seo/Breadcrumbs";
import JsonLd from "@/components/seo/JsonLd";
import RelatedPages from "@/components/marketing/RelatedPages";
import { breadcrumbsForPath } from "@/lib/seo/breadcrumbs";
import { pageGraph } from "@/lib/seo/graph";
import { offer, softwareApplication } from "@/lib/seo/schema";
import FancyButton from "@/components/ui/fancy-button";
import type { ToolContent } from "@/types/tools";
import { useRouter } from "next/router";

interface ToolPageProps {
  toolContent: ToolContent | null;
}

const ToolPage = ({ toolContent: initialToolContent }: ToolPageProps) => {
  const router = useRouter();
  const tool = typeof router.query.tool === "string" ? router.query.tool : "";
  const toolContent = initialToolContent ?? (tool ? (TOOL_CONTENT_MAP[tool] ?? null) : null);

  if (!toolContent) {
    if (!router.isReady) return null;

    return (
      <div className="flex min-h-[75vh] flex-col items-center justify-center">
        <h1 className="text-4xl font-bold text-gray-900">Page Not Found</h1>
        <p className="mt-4 text-lg text-gray-600">
          The feature you&apos;re looking for doesn&apos;t exist.
        </p>
        <FancyButton
          href="/"
          semiFancy
          className="mt-8 w-fit rounded-lg bg-blue-600 px-6 py-3 text-white hover:bg-blue-700"
        >
          Go back home
        </FancyButton>
      </div>
    );
  }

  const pageTitle = `${toolContent.seoTitle} | Crayo`;
  const path = `/features/free/${tool}`;
  const pageUrl = `https://crayo.ai${path}`;
  const crumbs = breadcrumbsForPath(path);

  return (
    <div className="flex size-full flex-col overflow-hidden bg-white transition-all duration-200 ease-in-out">
      <NextSeo
        title={pageTitle}
        description={toolContent.seoDescription}
        openGraph={{
          title: pageTitle,
          description: toolContent.seoDescription,
          url: pageUrl,
        }}
      />
      <JsonLd
        nodes={pageGraph({
          path,
          name: toolContent.seoTitle,
          description: toolContent.seoDescription,
          crumbs,
          faqs: toolContent.faqs,
          extra: [
            softwareApplication({
              name: toolContent.navTitle,
              url: pageUrl,
              description: toolContent.seoDescription,
              offers: offer({ name: "Free", price: 0 }),
            }),
          ],
        })}
      />
      <Breadcrumbs items={crumbs} />
      <ToolHero toolContent={toolContent} />
      <StepsSection />
      <FeaturesBento />
      <AutoClipperSection />
      <Testimonials />
      <CoFounder />
      <RelatedPages path={path} />
      <FAQ items={toolContent.faqs} />
      <Footer />
    </div>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = Object.keys(TOOL_CONTENT_MAP).map((tool) => ({
    params: { tool },
  }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<ToolPageProps> = async ({ params }) => {
  const tool = params?.tool as string;
  const toolContent = TOOL_CONTENT_MAP[tool] || null;

  return {
    props: {
      toolContent,
    },
  };
};

export default ToolPage;
